import { useRef } from 'react';
import { QueryObserverBaseResult } from 'react-query';

/**
 * Returns a referentially stable array of `useQueries` results.
 *
 * The returned array reference only changes when at least one item has a
 * meaningful state transition, detected by comparing three fields per item:
 *
 * - `dataUpdatedAt`  — bumped on every successful fetch; catches success transitions.
 * - `errorUpdatedAt` — bumped on every error; catches load-error and refetch-error transitions.
 * - `isFetching`     — flips true while any background refetch is in-flight; catches the
 *                      refetch start/end transitions that leave `status` unchanged.
 *
 * Together these three fields fully cover all five React Query observer states
 * (idle → loading → success | error, and success ↔ refetching).
 *
 * By stabilising the reference here, callers can safely write:
 * ```ts
 * const stableQueries = useStableQueries(rawQueries);
 * const result = useMemo(() => aggregate(stableQueries), [stableQueries]);
 * ```
 *
 * @template T - Any object that extends the minimal shape `{ dataUpdatedAt, errorUpdatedAt, isFetching }`,
 *               which every `UseQueryResult` satisfies.
 */
export const useStableQueries = <
  T extends Pick<QueryObserverBaseResult, 'dataUpdatedAt' | 'errorUpdatedAt' | 'isFetching'>
>(
  queries: T[]
): T[] => {
  const prevRef = useRef<T[]>(queries);
  const prev = prevRef.current;

  const hasChanged =
    prev.length !== queries.length ||
    queries.some(
      (q, i) =>
        q.dataUpdatedAt !== prev[i].dataUpdatedAt ||
        q.errorUpdatedAt !== prev[i].errorUpdatedAt ||
        q.isFetching !== prev[i].isFetching
    );

  if (hasChanged) {
    prevRef.current = queries;
  }

  return prevRef.current;
};
